class VagPersonalizationFields extends HTMLElement {
  #controller;
  #config;
  #scope;
  #generation = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#config = JSON.parse(this.querySelector('[data-vag-personalization-config]')?.textContent || '{}');
    this.#scope = this.closest('dialog, product-card, .shopify-section') || this.parentElement;
    const { signal } = this.#controller;
    this.addEventListener('input', () => this.refresh(), { signal });
    this.#scope?.addEventListener('change', this.#onChange, { signal, capture: true });
    this.#scope?.addEventListener('shopify:product:select', this.#onProductSelect, { signal });
    this.#scope?.addEventListener('submit', this.#onSubmit, { signal, capture: true });
    this.refresh();
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  get #picker() {
    return Array.from(this.#scope?.querySelectorAll('variant-picker') || []).find(
      (picker) => picker.dataset.productId === this.dataset.productId && picker.dataset.sectionId === this.dataset.sectionId
    );
  }

  get #form() {
    return Array.from(this.#scope?.querySelectorAll('form') || []).find((form) => form.getAttribute('id') === this.dataset.formId);
  }

  #selectedValue(option) {
    const picker = this.#picker;
    const checked = Array.from(picker?.querySelectorAll('input:checked[data-option-name]') || []).find(
      (input) => input.dataset.optionName === option.name
    );
    if (checked) return checked.value;
    for (const select of picker?.querySelectorAll('select') || []) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset.optionName === option.name || select.name === `options[${option.name}]`) {
        return selected?.value;
      }
    }
    return option.selected;
  }

  #onChange = (event) => {
    if (event.target.closest('variant-picker') === this.#picker) this.refresh();
  };

  #onProductSelect = (event) => {
    if (event.target.closest('variant-picker') !== this.#picker) return;
    const generation = ++this.#generation;
    this.refresh();
    event.promise?.then(() => {
      if (generation === this.#generation && this.isConnected) this.refresh();
    }).catch(() => {});
  };

  #onSubmit = (event) => {
    if (event.target !== this.#form) return;
    this.refresh();
    const invalid = Array.from(this.querySelectorAll('input')).find((input) => !input.checkValidity());
    if (invalid) {
      invalid.reportValidity();
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  };

  #setField(selector, enabled, required = false, maxLength) {
    const input = this.querySelector(selector);
    if (!input) return;
    input.disabled = !enabled;
    input.required = enabled && required;
    input.setAttribute('aria-required', String(input.required));
    if (maxLength) input.maxLength = maxLength;
    let message = '';
    if (input.required && !input.value.trim()) message = this.#config.requiredError;
    if (enabled && maxLength && input.value.length > maxLength) {
      message = this.#config.lengthError.replace('[max]', String(maxLength));
    }
    input.setCustomValidity(message);
  }

  refresh() {
    if (!this.#config) return;
    let policy = this.#config.initialPolicy || ['none', '100', 'false', 'false', 'false'];
    if (this.#config.mode !== 'keychain') {
      const option = this.#config.options?.[0];
      const selected = option?.values.find((value) => value.value === this.#selectedValue(option));
      if (selected) policy = selected.policy;
    }
    const enabled = policy[0] !== 'none';
    const limit = Number(policy[1]) || 100;
    const second = enabled && policy[2] === 'true';
    const thread = enabled && policy[3] === 'true' && !this.#config.colorOption;
    const photo = enabled && policy[4] === 'true';
    this.hidden = !enabled;
    const label = this.querySelector('[data-vag-text-label]');
    if (label) label.textContent = `${this.#config.labels[policy[0]] || this.#config.labels.text} *`;
    const help = this.querySelector('[data-vag-limit]');
    if (help) help.textContent = this.#config.limitText.replace('[max]', String(limit));
    this.#setField('[data-vag-text]', enabled, true, limit);
    this.#setField('[data-vag-second]', second, true, limit);
    this.#setField('[data-vag-thread]', thread);
    this.#setField('[data-vag-photo]', photo);
    for (const [selector, visible] of [
      ['[data-vag-second-field]', second],
      ['[data-vag-thread-field]', thread],
      ['[data-vag-photo-field]', photo],
    ]) {
      const field = this.querySelector(selector);
      if (field) field.hidden = !visible;
    }
    const text = this.querySelector('[data-vag-text]');
    const counter = this.querySelector('[data-vag-count]');
    if (counter && text) {
      counter.textContent = this.#config.counter.replace('[used]', String(text.value.length)).replace('[max]', String(limit));
    }
  }
}

if (!customElements.get('vag-personalization-fields')) {
  customElements.define('vag-personalization-fields', VagPersonalizationFields);
}
